import React, { useState } from "react";
import {
  Signature,
  Signatures,
} from "../../../../types/registerBranchDataTypes";

import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
  VisuallyHidden,
} from "@chakra-ui/react";
import { FiCheck, FiEdit2, FiPenTool, FiPlus, FiTrash2 } from "react-icons/fi";
import { useBranch } from "../../../../data/user";
import { DataTable, EmptyState } from "../../../ui";

interface signatureStepProps {
  savedData: Signatures;
  updateData: (data: Signatures) => void;
}

interface UpdatedSignature extends Signature {
  index: number;
}

const SignatureStep: React.FC<signatureStepProps> = ({
  savedData,
  updateData,
}) => {
  const toast = useToast();

  const [selectedUser, setSelectedUser] = useState<UpdatedSignature>({
    title: "",
    name: "",
    email: "",
    phone: "",
    mobile: "",
    index: -1,
  });

  const [editEnabled, setEditEnabled] = useState<boolean>(false);
  const { branch } = useBranch();

  return (
    <Box>
      {savedData && savedData.length > 0 ? (
        <DataTable size="sm" label="Signatories table">
          <Table>
            <Thead>
              <Tr>
                <Th scope="col">Title</Th>
                <Th scope="col">Name</Th>
                <Th scope="col">Email</Th>
                <Th scope="col">Phone</Th>
                <Th scope="col">Mobile</Th>
                <Th scope="col">
                  <VisuallyHidden>Actions</VisuallyHidden>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {savedData.map((user, index) => (
                <Tr key={index}>
                  <Td>{user.title}</Td>
                  <Td fontWeight={600}>{user.name}</Td>
                  <Td>{user.email}</Td>
                  <Td fontWeight={600}>{user.phone}</Td>
                  <Td fontWeight={600}>{user.mobile}</Td>

                  <Td>
                    <ButtonGroup
                      size="xs"
                      spacing={2}
                      display="flex"
                      justifyContent="flex-end"
                    >
                      <Button
                        variant="secondary"
                        leftIcon={<FiEdit2 />}
                        onClick={() => {
                          setSelectedUser({ ...user, index: index });
                          setEditEnabled(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger-soft"
                        leftIcon={<FiTrash2 />}
                        onClick={() => {
                          updateData(savedData.filter((_, i) => i !== index));
                        }}
                      >
                        Delete
                      </Button>
                    </ButtonGroup>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </DataTable>
      ) : (
        <EmptyState
          icon={FiPenTool}
          title="No signatures added"
          description='Fill in the form below and press "Add" to list an authorised signatory.'
          py={6}
          headingAs="p"
        />
      )}
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
        gap={5}
        mt={6}
      >
        <GridItem colSpan={{ base: 1, md: 3 }}>
          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input
              type="text"
              isInvalid={!selectedUser?.name} // validation
              value={selectedUser?.name}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  name: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Title</FormLabel>
            <Input
              type="text"
              isInvalid={!selectedUser?.title} // validation
              value={selectedUser?.title}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  title: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              isInvalid={!selectedUser?.email} // validation
              value={selectedUser?.email}
              placeholder={
                branch === "UAE" ? "test@test.com" : "Enter email address"
              }
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  email: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Phone</FormLabel>
            <Input
              type="number"
              isInvalid={!selectedUser?.phone}
              value={selectedUser?.phone}
              placeholder={
                branch === "UAE" ? "041234567" : "Enter phone number"
              }
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  phone: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Mobile</FormLabel>
            <Input
              type="number"
              value={selectedUser?.mobile}
              placeholder={
                branch === "UAE" ? "0501234567" : "Enter mobile number"
              }
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  mobile: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>

        <GridItem colSpan={1} alignSelf="end">
          <Button
            variant="secondary"
            w="full"
            leftIcon={editEnabled ? <FiCheck /> : <FiPlus />}
            isDisabled={
              !selectedUser?.name ||
              !selectedUser?.title ||
              !selectedUser?.email ||
              !selectedUser?.phone
            }
            onClick={() => {
              if (editEnabled) {
                // update the data and don't forget to remove the index  but after updating the data
                const { index, ...newUser } = selectedUser;
                updateData(
                  savedData.map((user, i) => (i === index ? newUser : user))
                );

                toast({
                  title: "Signature Updated",
                  description: "Signature has been updated successfully",
                  status: "warning",
                  duration: 9000,
                  isClosable: true,
                });

                setEditEnabled(false);
              } else {
                // remove the index before updating the data
                const { index, ...newUser } = selectedUser;
                updateData([...savedData, newUser]);

                toast({
                  title: "Signature Added",
                  description: "Signature has been added successfully",
                  status: "success",
                  duration: 9000,
                  isClosable: true,
                });
              }
              setSelectedUser({
                title: "",
                name: "",
                email: "",
                phone: 0,
                mobile: 0,
                index: -1,
              });
            }}
          >
            {editEnabled ? "Save" : "Add"}
          </Button>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default SignatureStep;
